import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { motion } from 'framer-motion';
import { Building2, Users, TrendingUp, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { db } from '@/lib/db';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DashboardStats {
  totalProperties: number;
  totalViews: number;
  totalLeads: number;
  conversionRate: number;
}

interface ChartData {
  date: string;
  views: number;
  leads: number;
}

interface LeadsBySource {
  source: string;
  count: number;
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    totalViews: 0,
    totalLeads: 0,
    conversionRate: 0,
  });
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [leadsBySource, setLeadsBySource] = useState<LeadsBySource[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch properties with retry logic
      const { data: propertiesData, error: propertiesError } = await db.fetch(
        supabase
          .from('properties')
          .select('*')
          .eq('user_id', user!.id)
          .order('created_at', { ascending: false })
      );

      if (propertiesError) throw propertiesError;

      // Fetch total views with retry logic
      const { count: totalViews } = await db.fetch(
        supabase
          .from('property_views')
          .select('*', { count: 'exact', head: true })
          .in('property_id', propertiesData?.map(p => p.id) || [])
          .eq('listing_type', 'property')
      );

      // Fetch total leads with retry logic
      const { count: totalLeads } = await db.fetch(
        supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .in('property_id', propertiesData?.map(p => p.id) || [])
          .eq('listing_type', 'property')
      );

      // Calculate conversion rate
      const conversionRate = totalViews ? (totalLeads / totalViews) * 100 : 0;

      setStats({
        totalProperties: propertiesData?.length || 0,
        totalViews: totalViews || 0,
        totalLeads: totalLeads || 0,
        conversionRate,
      });

      // Fetch chart data for the last 7 days with retry logic
      const chartData = await Promise.all(
        Array.from({ length: 7 }).map(async (_, i) => {
          const date = subDays(new Date(), i);
          const start = startOfDay(date);
          const end = endOfDay(date);

          const { count: views } = await db.fetch(
            supabase
              .from('property_views')
              .select('*', { count: 'exact', head: true })
              .in('property_id', propertiesData?.map(p => p.id) || [])
              .eq('listing_type', 'property')
              .gte('viewed_at', start.toISOString())
              .lte('viewed_at', end.toISOString())
          );

          const { count: leads } = await db.fetch(
            supabase
              .from('leads')
              .select('*', { count: 'exact', head: true })
              .in('property_id', propertiesData?.map(p => p.id) || [])
              .eq('listing_type', 'property')
              .gte('created_at', start.toISOString())
              .lte('created_at', end.toISOString())
          );

          return {
            date: format(date, 'dd/MM', { locale: ptBR }),
            views: views || 0,
            leads: leads || 0,
          };
        })
      );

      setChartData(chartData.reverse());

      // Fetch leads by source with retry logic
      const { data: leadSourceData } = await db.fetch(
        supabase
          .from('leads')
          .select('source')
          .in('property_id', propertiesData?.map(p => p.id) || [])
          .eq('listing_type', 'property')
      );

      const sourceCount = leadSourceData?.reduce((acc, curr) => {
        acc[curr.source] = (acc[curr.source] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      setLeadsBySource(
        Object.entries(sourceCount || {}).map(([source, count]) => ({
          source,
          count,
        }))
      );

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erro ao carregar estatísticas. Tentando novamente...');
      
      // Retry after a delay
      setTimeout(fetchDashboardData, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleExternalSiteClick = () => {
    if (!user?.slug) {
      toast.error('Configure seu perfil primeiro para ter acesso ao site público');
      navigate('/dashboard/settings');
      return;
    }
    window.open(`https://imoveis.vitrineturbo.com/${user.slug}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Bem-vindo de volta, {user?.name}
          </p>
        </div>

        <Button 
          onClick={handleExternalSiteClick}
          className="mt-4 md:mt-0"
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          Ver Minha Vitrine
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total de Imóveis</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalProperties}</div>
            <p className="text-xs text-muted-foreground">Imóveis cadastrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Visualizações</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalViews}</div>
            <p className="text-xs text-muted-foreground">Total de visualizações</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLeads}</div>
            <p className="text-xs text-muted-foreground">Contatos recebidos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.conversionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">Visualizações para leads</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Visualizações e Leads</CardTitle>
            <p className="text-sm text-muted-foreground">
              Últimos 7 dias
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="views"
                    stroke="#8884d8"
                    name="Visualizações"
                  />
                  <Line
                    type="monotone"
                    dataKey="leads"
                    stroke="#82ca9d"
                    name="Leads"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leads por Origem</CardTitle>
            <p className="text-sm text-muted-foreground">
              Distribuição dos contatos
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsBySource}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}